<script lang="ts" setup>
import EditIcon from "@/components/icon/editIcon.vue";
import { useGender, useMateTypeText } from "@/use/common";
import { useMate } from "@/stores/mate";
import { ModuleFieldItem, useModule } from "@/stores/module";
import { BASIC_INFO_MODULE_ID } from "@/stores/module/moduleMate";
import { computed, ref } from "vue";
import i18n from "@/locales";
import { Mate, Module } from "@/types/entity";
import { usePresetTags } from "@/composable/settings/tag";
import { useRouter } from "vue-router";
import { getNameFromMbtiType } from "@/assets/const/mbti";
// import { useAdmire } from "@/stores/admire";
// import { useGlobalUser } from "@/stores/globalUser";

export interface MateProfileProps {
  mate: Mate;
  basicInfo: Pick<Mate, "name" | "type" | "age" | "bio">;
  hasProfileAuth: boolean;
}

const props = defineProps<MateProfileProps>();

const mateStore = useMate();
const moduleStore = useModule();
const router = useRouter();

const moduleFieldsMap = computed<{ [moduleId: number]: ModuleFieldItem[] }>(
  () => {
    return moduleStore.module!.moduleFieldsMap;
  }
);

const moduleMap = computed<{ [moduleId: number]: Module }>(() => {
  return moduleStore.module!.moduleIdMap;
});

const customModuleMap = computed<{ [moduleId: number]: ModuleFieldItem[] }>(
  () => {
    const map: { [moduleId: number]: ModuleFieldItem[] } = {};
    if (!moduleFieldsMap.value) return map;
    const { moduleOrder } = props.mate;
    const idChildrenMap = new Map<number, number[]>();
    if (moduleOrder?.length > 1 && moduleOrder[0].id === BASIC_INFO_MODULE_ID) {
      moduleOrder.forEach((item) => {
        const { id, children } = item;
        if (children.length > 0) {
          idChildrenMap.set(id, children);
        }
      });
    }

    Object.entries(moduleFieldsMap.value).forEach(
      (entry: [string, ModuleFieldItem[]]) => {
        const moduleId = Number(entry[0]);
        const value = entry[1].filter((item) => item.value);
        if (idChildrenMap.has(moduleId)) {
          const children = idChildrenMap.get(moduleId)!;
          value.sort((a, b) => {
            const aIndex = children.indexOf(a.id);
            const bIndex = children.indexOf(b.id);
            return aIndex - bIndex;
          });
        }
        if (Number(moduleId) !== BASIC_INFO_MODULE_ID && value.length > 0) {
          map[moduleId] = value;
        }
      }
    );
    return map;
  }
);
const fieldList: (keyof Mate)[] = [
  "name",
  "gender",
  "type",
  "worldview",
  "mbti",
  "relationWithMe",
  "birthday",
  "race",
  "occupation",
  "bio",
  "personalityPrompt",
  "tag",
];

const { mergedTagObj } = usePresetTags();
const getBasicInfoFieldItem = (field: keyof Mate): ModuleFieldItem => {
  let value = props.mate[field];
  if (field === "type") {
    value = useMateTypeText(value);
  } else if (field === "gender") {
    value = useGender(value);
  } else if (field === "worldview") {
    value = value?.name;
  } else if (field === "tag") {
    value = value
      ?.split(",")
      .map((tag: string) => mergedTagObj.value[tag]?.label || tag)
      .join(", ");
  } else if (field === "mbti" && value) {
    value = getNameFromMbtiType(value);
  }
  if (value === "<p></p>") value = "";

  return {
    id: 0,
    text: i18n.global.t(`User.mate.basicInfo.${field}`),
    displayName: i18n.global.t(`User.mate.basicInfo.${field}`),
    value,
    moduleId: BASIC_INFO_MODULE_ID,
  };
};

const loading = ref<boolean>(false);
// const hasProfileAuth = ref(false);
// Lifecycle
// const prepareData = async () => {
//   loading.value = true;
//   const { visibility, user, uuid } = props.mate;

//   if (mate.isMyMate(uuid) || visibility === 1) {
//     hasProfileAuth.value = true;
//   } else if (visibility === 4 && useGlobalUser().isLogin) {
//     // 检查是不是好友
//     const admireRecord = await useAdmire().isAdmiring(user.userMate!.id!);
//     if (admireRecord && admireRecord.status === undefined) {
//       // 是好友
//       hasProfileAuth.value = true;
//     }
//   }

//   loading.value = false;

//   if (!hasProfileAuth.value) {
//     return (loading.value = false);
//   }
// };
// onBeforeMount(() => {
//   prepareData();
// });
</script>
<template>
  <div class="pt-2 pb-4">
    <div v-if="loading" class="flex flex-col items-center justify-center">
      <span class="loading" />
    </div>
    <div v-else-if="!hasProfileAuth">
      <VanEmpty :description="$t(`Message.noPermissionToViewRole`)" />
    </div>
    <div v-else class="flex flex-col">
      <div>
        <VanCellGroup inset>
          <template #title>
            <div class="flex flex-row justify-between items-center">
              <div class="text-base-content/50">
                {{ $t("User.mate.basicInfo.title") }}
              </div>
              <button
                v-if="mateStore.currentMate?.uuid === props.mate.uuid"
                class="btn btn-xs btn-ghost text-base-content/50"
                @click="
                  router.push({
                    name: 'mateEdit',
                    params: { uuid: mateStore.currentMate!.uuid },
                    hash: `#${BASIC_INFO_MODULE_ID}`,
                  })
                "
              >
                <edit-icon />
              </button>
            </div>
          </template>
          <!-- @vue-ignore -->
          <template v-for="(item, idx) in fieldList">
            <MateProfileCell
              v-if="getBasicInfoFieldItem(item).value"
              :key="idx"
              :item="getBasicInfoFieldItem(item)"
            />
          </template>

          <template
            v-if="moduleFieldsMap && moduleFieldsMap[BASIC_INFO_MODULE_ID]"
          >
            <template
              v-for="(item, idx) in moduleFieldsMap[BASIC_INFO_MODULE_ID]"
            >
              <MateProfileCell v-if="item.value" :key="idx" :item="item" />
            </template>
          </template>
        </VanCellGroup>
      </div>
      <VanCellGroup
        v-for="(fields, moduleId) in customModuleMap"
        :key="moduleId"
        inset
      >
        <template #title>
          <div class="flex flex-row justify-between items-center">
            <div class="text-base-content/50">
              {{ moduleMap[moduleId].name }}
            </div>
            <button
              v-if="mateStore.currentMate?.uuid === props.mate.uuid"
              class="btn btn-xs btn-ghost text-base-content/50"
              @click="
                router.push({
                  name: 'mateEdit',
                  params: { uuid: mateStore.currentMate!.uuid },
                  hash: `#${moduleId}`,
                })
              "
            >
              <edit-icon />
            </button>
          </div>
        </template>
        <MateProfileCell
          v-for="(item, idx) in fields"
          :key="idx"
          :item="item"
        />
      </VanCellGroup>
    </div>
  </div>
</template>

<style scoped></style>
